import { Link } from '@tanstack/react-router';
import {
  CircleArrowLeft,
  Clock,
  Download,
  Loader2,
  Lock,
  Pause,
} from 'lucide-react';

import { InputDatePicker } from '~shared/components/input-date-picker';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
} from '~shared/shadcn/ui/card';

import {
  formatMoney,
  formatNextRunAt,
  formatScheduledRunEastern,
  formatScheduledRunHeaderLine,
} from './payment-run-format';

interface PostponeButtonState {
  disabled: boolean;
  title?: string;
  statusHint: string | null;
}

interface UnlockMutationLike {
  mutate: () => void;
  isPending: boolean;
}

interface PaymentRunReviewToolbarProps {
  studioName?: string;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  nextRunAt: string | undefined;
  countdown: string;
  readyCount: number | undefined;
  readySum: number | undefined;
  flaggedCount: number | undefined;
  flaggedSum: number | undefined;
  removedCount: number | undefined;
  removedSum: number | undefined;
  totalCount: number | undefined;
  netSum: number | undefined;
  currencySymbol: string;
  postponeButtonState: PostponeButtonState;
  postponeLimitReached: boolean;
  postponeCountMax: number;
  openPostponeModal: () => void;
  onDownloadCsv: () => void;
  lockInteractionsDisabled: boolean;
  incrementalLockAllowed: boolean;
  approveChargesToProcess: number | undefined;
  onOpenApproveModal: () => void;
  canUnlockRun: boolean;
  paymentRunApproveLockEnabled: boolean;
  paymentRunPostponeCronEnabled: boolean;
  unlockMutation: UnlockMutationLike;
  isRunLocked: boolean;
  approvedInvoiceIdsForRun: Array<string> | undefined;
  isPostponed: boolean;
}

export function PaymentRunReviewToolbar({
  studioName,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  nextRunAt,
  countdown,
  readyCount,
  readySum,
  flaggedCount,
  flaggedSum,
  removedCount,
  removedSum,
  totalCount,
  netSum,
  currencySymbol: symbol,
  postponeButtonState,
  postponeLimitReached,
  postponeCountMax,
  openPostponeModal,
  onDownloadCsv,
  lockInteractionsDisabled,
  incrementalLockAllowed,
  approveChargesToProcess,
  onOpenApproveModal,
  canUnlockRun,
  paymentRunApproveLockEnabled,
  paymentRunPostponeCronEnabled,
  unlockMutation,
  isRunLocked,
  approvedInvoiceIdsForRun,
  isPostponed,
}: PaymentRunReviewToolbarProps) {
  return (
    <>
      <div className='flex items-center gap-4'>
        <Link
          to='/payment/payment-dashboard'
          className='flex items-center gap-2 text-muted-foreground hover:text-foreground'
        >
          <CircleArrowLeft className='size-5' />
          <span className='text-xl font-semibold'>Payment Run Review</span>
        </Link>
      </div>

      <div className='flex w-full min-w-0 flex-col gap-2 text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2'>
        <div className='flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3'>
          <span className='font-medium text-foreground'>
            {studioName ?? 'Studio'}
          </span>
          <div className='flex min-w-0 items-center gap-2'>
            <InputDatePicker
              id='payment-run-start-date'
              value={startDate}
              onChange={setStartDate}
              ariaLabel='Start date'
              align='start'
              displayFormat='MM/dd/yyyy'
              className='h-8 min-w-0 flex-1 px-2 sm:w-40 sm:flex-none'
            />
            <span className='shrink-0'>to</span>
            <InputDatePicker
              id='payment-run-end-date'
              value={endDate}
              onChange={setEndDate}
              ariaLabel='End date'
              align='end'
              displayFormat='MM/dd/yyyy'
              className='h-8 min-w-0 flex-1 px-2 sm:w-40 sm:flex-none'
            />
          </div>
        </div>
        {nextRunAt ? (
          <div
            className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-l border-border pl-3 sm:ml-auto'
            role='status'
            aria-label='Scheduled payment run'
          >
            <span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Scheduled run
            </span>
            <span className='text-sm font-medium text-foreground'>
              {formatScheduledRunHeaderLine(nextRunAt)}
            </span>
          </div>
        ) : null}
      </div>

      <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
        <Card className='border-l-4 border-l-emerald-500'>
          <CardContent className='p-4'>
            <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Ready to process
            </div>
            <div className='text-2xl font-semibold'>{readyCount ?? '—'}</div>
            <div className='font-semibold text-emerald-600'>
              {readySum != null ? formatMoney(readySum, symbol) : '—'}
            </div>
          </CardContent>
        </Card>
        <Card className='border-l-4 border-l-amber-500'>
          <CardContent className='p-4'>
            <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Flagged for review
            </div>
            <div className='text-2xl font-semibold text-amber-600'>
              {flaggedCount ?? '—'}
            </div>
            <div className='font-medium'>
              {flaggedSum != null ? formatMoney(flaggedSum, symbol) : '—'}
            </div>
          </CardContent>
        </Card>
        <Card className='border-l-4 border-l-destructive'>
          <CardContent className='p-4'>
            <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Auto-removed
            </div>
            <div className='text-2xl font-semibold text-destructive'>
              {removedCount ?? '—'}
            </div>
            <div className='font-medium'>
              {removedSum != null ? formatMoney(removedSum, symbol) : '—'}
            </div>
            <p className='mt-1 text-xs text-muted-foreground'>
              Charges kept out of this run — not owed to families and not pending
              collection.
            </p>
          </CardContent>
        </Card>
        <Card className='border-l-4 border-l-yellow-500'>
          <CardContent className='p-4'>
            <div className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
              Net run total
            </div>
            <div className='text-2xl font-semibold text-yellow-600'>
              {netSum != null ? formatMoney(netSum, symbol) : '—'}
            </div>
            <div className='text-sm text-muted-foreground'>
              {totalCount != null && readyCount != null
                ? `${readyCount} of ${totalCount} charges`
                : '—'}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className='space-y-3 rounded-lg border bg-card p-4'>
        <div className='flex flex-wrap items-center gap-4'>
          <span className='inline-flex items-center gap-2 text-sm text-muted-foreground'>
            <Clock className='size-4' /> Auto-runs in {countdown}
          </span>
          <div className='flex-1' />
          <Button
            variant='outline'
            size='sm'
            disabled={postponeButtonState.disabled}
            onClick={openPostponeModal}
            title={postponeButtonState.title}
          >
            <Pause className='mr-2 size-4' aria-hidden />
            Postpone 24h
          </Button>
          {postponeLimitReached && (
            <span className='text-sm text-muted-foreground' role='status'>
              Postpone limit reached ({postponeCountMax} per run cycle)
            </span>
          )}
          {!postponeLimitReached &&
          postponeButtonState.disabled &&
          postponeButtonState.statusHint ? (
            <span className='text-sm text-muted-foreground' role='status'>
              {postponeButtonState.statusHint}
            </span>
          ) : null}
          <Button variant='outline' size='sm' onClick={onDownloadCsv}>
            <Download className='mr-2 size-4' />
            Download CSV
          </Button>
          <Button
            size='sm'
            className='bg-amber-500 hover:bg-amber-600'
            disabled={
              lockInteractionsDisabled ||
              incrementalLockAllowed ||
              !paymentRunApproveLockEnabled ||
              (approveChargesToProcess ?? 0) === 0 ||
              !nextRunAt
            }
            onClick={onOpenApproveModal}
            title={
              incrementalLockAllowed
                ? 'Use the table checkboxes and Approve & Lock Run below to add more charges to this lock'
                : undefined
            }
          >
            <Lock className='mr-2 size-4' aria-hidden />
            Approve &amp; Lock Run
          </Button>
          {canUnlockRun && (
            <Button
              variant='outline'
              size='sm'
              disabled={
                unlockMutation.isPending || !paymentRunApproveLockEnabled
              }
              onClick={() => {
                unlockMutation.mutate();
              }}
            >
              {unlockMutation.isPending ? (
                <Loader2 className='mr-2 size-4 animate-spin' aria-hidden />
              ) : null}
              Unlock run
            </Button>
          )}
        </div>
        {!paymentRunPostponeCronEnabled ? (
          <p
            className='border-t border-border pt-3 text-sm text-muted-foreground'
            role='status'
          >
            Postpone is unavailable. On Manual Review, enable &quot;Apply
            postponement in scheduled processing&quot; in Payment &amp; Billing
            Settings. Approve &amp; Lock and Unlock are still available.
          </p>
        ) : null}
      </div>

      {isRunLocked && (
        <div className='flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900'>
          <Lock className='size-5 shrink-0 text-amber-700' aria-hidden />
          <span>
            <strong>Approved and locked</strong>
            {approvedInvoiceIdsForRun &&
            approvedInvoiceIdsForRun.length > 0 ? (
              <>
                {' '}
                — <strong>{approvedInvoiceIdsForRun.length}</strong> charge
                {approvedInvoiceIdsForRun.length !== 1 ? 's' : ''} will be
                charged on the next scheduled payment run (even if due later).
                {incrementalLockAllowed ? (
                  <>
                    {' '}
                    <strong>Postpone</strong> only skips charges that are not
                    approved & locked.
                  </>
                ) : null}
              </>
            ) : null}
            {!canUnlockRun && nextRunAt ? (
              <>
                {' '}
                Unlock until 30 minutes before the run (
                {formatScheduledRunEastern(nextRunAt)}).
              </>
            ) : null}
          </span>
        </div>
      )}

      {isPostponed && nextRunAt && (
        <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900'>
          <Pause className='size-5 shrink-0 text-amber-600' aria-hidden />
          <span>
            This payment run has been postponed. Charges will run at{' '}
            <strong>{formatNextRunAt(nextRunAt)}</strong>.
          </span>
        </div>
      )}
    </>
  );
}
